import React, {
  useMemo,
  useRef,
  useEffect,
  lazy,
  Suspense,
  memo,
} from "react";
import { Link } from "react-router-dom";
import { isMobileOnly } from "react-device-detect";
import { Swiper, SwiperSlide } from "swiper/react";
import SwiperCore, {
  Autoplay,
  EffectFade,
  Keyboard,
  Mousewheel,
  Pagination,
} from "swiper/core";
import { VideoTag } from "./common/VideoTag";
import { SliderEntity } from "../types/strapi";
import { siteUrl, swiperDelay } from "../utils/constants";
import { imgSize } from "../utils/funcs";
import { sortByOrderAttribute, cleanupClasses } from "../utils/homeUtils";

// Import Swiper styles
import "swiper/swiper.min.css";
import "swiper/components/effect-fade/effect-fade.min.css";
import "swiper/components/pagination/pagination.min.css";

// Import lazy components
const Header = lazy(() => import("./common/Header"));
const Helmet = lazy(() => import("./common/Helmet"));

// Initialize Swiper modules
SwiperCore.use([Autoplay, EffectFade, Keyboard, Mousewheel, Pagination]);

interface HomeProps {
  home: SliderEntity[];
}

const Home: React.FC<HomeProps> = ({ home }) => {
  const htmlRef = useRef(document.documentElement);
  const bodyRef = useRef(document.body);
  const rootRef = useRef(document.getElementById("root"));

  useEffect(() => {
    const rootTag = rootRef.current;
    const currentHtmlRef = htmlRef.current;
    const currentBodyRef = bodyRef.current;

    if (rootTag) {
      rootTag.classList.add("rootClass");
    }
    currentHtmlRef.classList.add("superClass");
    currentBodyRef.classList.add("superClass");

    return () => {
      cleanupClasses([rootTag, currentHtmlRef, currentBodyRef].filter(Boolean));
    };
  }, [rootRef, htmlRef, bodyRef]);

  const sortedHomeItems = useMemo(() => {
    if (home?.length > 0) {
      return [...home].sort(sortByOrderAttribute);
    }
    return [];
  }, [home]);

  return (
    <>
      <Suspense fallback={<div>Loading...</div>}>
        <Helmet pageTitle="Home" />
        <Header />
      </Suspense>
      <Swiper
        pagination={{ clickable: true }}
        keyboard={{ enabled: true }}
        autoplay={{ delay: swiperDelay, disableOnInteraction: false }}
        effect="fade"
        className="mySwiper"
        allowSlideNext
        loop
        mousewheel
      >
        {sortedHomeItems.map((item) => (
          <SwiperSlide key={item.id}>
            <div
              className="slide slide-style-2 slider-video-bg d-flex align-items-center justify-content-center"
              data-black-overlay="4"
            >
              <div className="container">
                <div className="row align-items-center">
                  <div className="col-lg-12">
                    <div className="inner pt_sm--40 text-center">
                      {item?.heading ? (
                        <h1 className="title">{item.heading}</h1>
                      ) : null}
                      {item?.text ? (
                        <p className="description">{item.text}</p>
                      ) : null}
                      {item?.preamble ? (
                        <span className="theme-color font-700 mt--20">
                          {item.preamble}
                        </span>
                      ) : null}
                      <Link
                        to={item?.url || "#"}
                        className="btn-default mt--30 size-md"
                        role="button"
                        tabIndex={0}
                      >
                        <span>Read More</span>
                      </Link>
                    </div>
                  </div>
                </div>
              </div>

              <div className="video-background">
                {item?.media?.mime?.includes(
                  "video"
                ) ? (
                  <VideoTag
                    autoPlay
                    muted
                    playsInline
                    loop
                    src={`${new URL(
                      item?.media?.url,
                      siteUrl
                    ).toString()}`}
                    controls={false}
                  />
                ) : (
                  <img
                    src={imgSize(item?.media, isMobileOnly)}
                    alt="case background"
                    className="bg-img"
                  />
                )}
              </div>
            </div>
          </SwiperSlide>
        ))}
      </Swiper>
    </>
  );
};

export default memo(Home);
